// src/components/Admin/ItemManager/ItemTransactionLog.jsx
import { useEffect, useMemo, useState } from 'react';
import {
    Box, Table, TableHead, TableRow, TableCell, TableBody,
    CircularProgress, Paper, Chip, Pagination, Select, MenuItem,
    Stack, Typography, TextField, useTheme, useMediaQuery, Divider,
} from '@mui/material';
import api from '../../../api/axios';

const STATUS_LIST = ['전체', '미사용', '사용', '승인대기', '환불됨'];

const displayStatus = (raw) => {
    switch (raw) {
        case '사용완료': return '사용';
        case '승인대기': return '승인대기';
        case '환불됨': return '환불됨';
        case '미사용': return '미사용';
        default: return raw;
    }
};

const statusColor = (st) => ({
    미사용: 'default',
    사용: 'success',
    승인대기: 'warning',
    환불됨: 'info',
}[String(st || '').trim()] || 'default');

export default function ItemTransactionLog() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('전체');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [selectedDate, setSelectedDate] = useState('');

    const rowsPerPage = 10;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    /* ───── 데이터 로드 ───── */
    const fetchLogs = () => {
        setLoading(true);
        api.get('/api/rewards/item-admin/purchase-log/')
            .then((res) => setLogs(res.data.logs || []))
            .catch(() => alert('구매 로그를 불러오는 데 실패했습니다.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchLogs(); }, []);

    /* ───── 필터링 (useMemo) ───── */
    const filteredLogs = useMemo(() => {
        const keyword = searchKeyword.trim().toLowerCase();

        return logs
            .filter((l) => {
                const st = displayStatus(l.status || '').trim();
                return statusFilter === '전체' || st === statusFilter;
            })
            .filter((l) => {
                const name = (l.username || '').toLowerCase();
                const item = (l.item_name || '').toLowerCase();
                const keywordMatch = !keyword || name.includes(keyword) || item.includes(keyword);

                const purchasedAt = l.purchased_at || '';
                const dateMatch = !selectedDate || purchasedAt.startsWith(selectedDate);

                return keywordMatch && dateMatch;
            });
    }, [logs, statusFilter, searchKeyword, selectedDate]);

    /* ───── 페이지네이션 + 범위 보정 ───── */
    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / rowsPerPage));

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const paginated = useMemo(() => (
        filteredLogs.slice((page - 1) * rowsPerPage, page * rowsPerPage)
    ), [filteredLogs, page]);

    if (loading) return <CircularProgress sx={{ mt: 4 }} />;

    return (
        <Box sx={{ width: '100%', maxWidth: '100%' }}>
            {/* 검색 · 필터 바 */}
            <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={2}
                sx={{ mb: 2 }}
                alignItems={isMobile ? 'stretch' : 'center'}
            >
                <TextField
                    size="small"
                    label="검색 (닉네임 / 아이템명)"
                    value={searchKeyword}
                    onChange={(e) => { setSearchKeyword(e.target.value); setPage(1); }}
                    fullWidth={isMobile}
                />

                <Select
                    size="small"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    sx={{ minWidth: isMobile ? '100%' : 140 }}
                >
                    {STATUS_LIST.map((l) => (
                        <MenuItem key={l} value={l}>{l}</MenuItem>
                    ))}
                </Select>

                <TextField
                    type="date"
                    size="small"
                    label="날짜"
                    InputLabelProps={{ shrink: true }}
                    value={selectedDate}
                    onChange={(e) => { setSelectedDate(e.target.value); setPage(1); }}
                    fullWidth={isMobile}
                />

                <Typography
                    variant="body2"
                    sx={{
                        ml: isMobile ? 0 : 'auto',
                        alignSelf: isMobile ? 'flex-end' : 'center',
                        color: 'text.secondary',
                    }}
                >
                    총 {filteredLogs.length}건
                </Typography>
            </Stack>

            {filteredLogs.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography sx={{ color: 'text.secondary' }}>
                        🔍 조건에 맞는 로그가 없습니다.
                    </Typography>
                </Paper>
            ) : (
                <>
                    {/* ✅ 모바일: 카드형 */}
                    {isMobile ? (
                        <Stack spacing={1.25}>
                            {paginated.map((log) => {
                                const st = displayStatus(log.status);
                                return (
                                    <Paper key={log.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                            <Typography sx={{ fontWeight: 800, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {log.item_name}
                                            </Typography>
                                            <Chip
                                                label={st}
                                                color={statusColor(st)}
                                                size="small"
                                                sx={{ flex: 'none' }}
                                            />
                                        </Stack>

                                        <Divider sx={{ my: 1 }} />

                                        <Stack spacing={0.6}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body2" color="text.secondary">구매자</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{log.username}</Typography>
                                            </Stack>

                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body2" color="text.secondary">가격</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                                    {log.price ?? 0} Leaf🌿
                                                </Typography>
                                            </Stack>

                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body2" color="text.secondary">구매일시</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                    {log.purchased_at}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                );
                            })}

                            {totalPages > 1 && (
                                <Box mt={0.5} display="flex" justifyContent="center">
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={(_, v) => setPage(v)}
                                        color="primary"
                                        showFirstButton
                                        showLastButton
                                        size="small"
                                        shape="rounded"
                                    />
                                </Box>
                            )}
                        </Stack>
                    ) : (
                        /* ✅ PC: 테이블 유지 (가로스크롤 최소화) */
                        <>
                            <Paper variant="outlined" sx={{ width: '100%', overflowX: 'auto' }}>
                                <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>아이템명</TableCell>
                                            <TableCell>구매자</TableCell>
                                            <TableCell>가격</TableCell>
                                            <TableCell>구매일시</TableCell>
                                            <TableCell>상태</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {paginated.map((log) => {
                                            const st = displayStatus(log.status);
                                            return (
                                                <TableRow key={log.id} hover>
                                                    <TableCell>{log.item_name}</TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.username}</TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.price} Leaf🌿</TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.purchased_at}</TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                        <Chip
                                                            label={st}
                                                            color={statusColor(st)}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Paper>

                            {totalPages > 1 && (
                                <Box mt={2} display="flex" justifyContent="center">
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={(_, v) => setPage(v)}
                                        color="primary"
                                        showFirstButton
                                        showLastButton
                                        shape="rounded"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </>
            )}
        </Box>
    );
}
